import { ref, computed } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { useEventBus } from '@vueuse/core'
import { useUserStore } from '@/stores'

export default defineStore('session', () => {
  const user = useUserStore()
  const info = ref({})
  const type = import.meta.env.VITE_APP_TYPE
  const {id, fullName, profile, party} = storeToRefs(user)
  const conference = ref(null)
  const key = ref(null)
  const remoteParticipants = ref([])
  const participants = computed(() => [
    ...remoteParticipants.value,
    {
      id: id.value,
      fullName: fullName.value,
      profile: profile.value,
      party: party.value,
    },
  ])
  const participantsLeave = ref([])
  const connections = ref([])
  const configuration = ref(null)
  const startTimestamp = ref(null)
  const duration = ref(0)
  const sessionFailed = ref(false)

  const users = computed(() => [user, ...participants.value])
  const hasClient = computed(() =>
    participants.value.some(participant => participant.party === 'client' && participant.profile)
  )
  const hasAssistant = computed(() => participants.value.some(participant => participant.party === 'assistant'))
  const hasObserver = computed(() => participants.value.some(participant => participant.party === 'observer'))

  const client = computed(() => participants.value.find(participant => participant.party === 'client'))
  const assistant = computed(() => participants.value.find(participant => participant.party === 'assistant'))
  const observers = computed(() => participants.value.find(participant => participant.party === 'observer'))

  const transform = computed(() => {
    if (type.value === 'client') return false
    let {clientWidth: width, clientHeight: height} = document.documentElement
    if (typeof client.value?.profile?.viewport === 'object') {
      const {width: w, height: h} = client.value.profile.viewport
      width = w
      height = h
    }
    const {width: userWidth, height: userHeight} = user.profile.viewport
    const scale = Math.min(userWidth / width, userHeight / height)
    const isWider = width / height > userWidth / userHeight
    const top = isWider ? (userHeight - height * scale) / 2 : 0
    const left = isWider ? 0 : (userWidth - width * scale) / 2
    return {
      scale,
      left,
      top,
      isWider,
      client: {
        width,
        height,
      },
      user: {
        width: userWidth,
        height: userHeight,
      },
    }
  })

  const hasTorchCapability = computed(() => {
    if (participants.value.length) {
      const client = participants.value.find(participant => participant.party === 'client')
      return client?.profile?.hasTorchCapability
    }
    return false
  })

  const notClientCamera = computed(() => client.value && client.value.profile?.camera !== 'on')

  const notClientPresent = computed(() => client.value && client.value.profile?.presence === false)

  const torchStatus = computed(() => {
    if (participants.value.length) {
      const client = participants.value.find(participant => participant.party === 'client')
      return client?.profile?.torchStatus
    }
    return false
  })

  const time = computed(() => new Date(duration.value * 1000).toISOString().substring(11, 19))

  let interval = 0

  function createParticipant(participant) {
    this.info.participants.push(participant)
  }

  function addParticipant(participant) {
    const currentParticipant = remoteParticipants.value.find(
      p => p.party === participant.party && p.id === participant.id
    )
    if (!currentParticipant) {
      remoteParticipants.value.push(participant)
    }
  }

  function removeParticipant(participantId) {
    remoteParticipants.value = remoteParticipants.value.filter(
        participant => Number(participant.id) !== Number(participantId)
      )
  }

  function getParticipant(participantId) {
    return participants.value.find(participant => Number(participant.id) === Number(participantId))
  }

  function getParticipantInfo(participantId) {
    return this.info.participants.find(participant => Number(participant.id) === Number(participantId))
  }

  function setInfo(value) {
    info.value = value
  }

  function setConfiguration(value) {
    configuration.value = value
  }

  function addParticipantsLeave(participantId) {
    this.participantsLeave.push(participantId)
  }

  function removeParticipantsLeave(participantId) {
    this.participantsLeave = [...this.participantsLeave.filter(participant => participant !== participantId)]
  }

  function setStartTimestamp(value) {
    startTimestamp.value = value
  }

  // Static and dynamic profile arrive in separate messages and fill the same object
  function updateParticipantProfile(id, profile) {
    const participant = getParticipant(id)
    if (participant) participant.profile = {...participant.profile, ...profile}
  }

  function updateParticipant(id, data) {
    const participant = getParticipant(id)
    if (participant) Object.assign(participant, data)
  }

  useEventBus('profile:static').on(({id, deviceInfo}) => updateParticipantProfile(id, deviceInfo))
  useEventBus('profile:sync').on(({id, profile}) => updateParticipantProfile(id, profile))

  function updateConnections(data) {
    this.connections = data
  }

  function startTimer() {
    clearInterval(interval)
    interval = setInterval(() => {
      duration.value = (Date.now() - startTimestamp.value) / 1000
    }, 1000)
  }

  function stopTimer() {
    clearInterval(interval)
  }

  return {
    info,
    remoteParticipants,
    conference,
    key,
    participants,
    participantsLeave,
    users,
    connections,
    configuration,
    sessionFailed,
    hasClient,
    hasAssistant,
    hasObserver,
    client,
    assistant,
    observers,
    transform,
    startTimestamp,
    duration,
    time,
    createParticipant,
    addParticipant,
    addParticipantsLeave,
    removeParticipantsLeave,
    removeParticipant,
    getParticipant,
    getParticipantInfo,
    hasTorchCapability,
    notClientCamera,
    notClientPresent,
    torchStatus,
    setInfo,
    setConfiguration,
    updateParticipantProfile,
    updateParticipant,
    updateConnections,
    startTimer,
    stopTimer,
    setStartTimestamp,
  }
})
